import { useContext, useRef, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import Radar from 'radar-sdk-js';
import EventStepFooter from '../../components/host/EventStepFooter';
import HostBasicInfo from '../../components/host/HostBasicInfo';
import HostAgenda from '../../components/host/HostAgenda';
import axios from 'axios';

/* ======================
   OPTIONS (chips / selects)
====================== */

const PURPOSE_OPTIONS = [
  { id: 'friendship', label: '🫶 Friendship' },
  { id: 'dating', label: '💘 Dating' },
  { id: 'networking', label: '🤝 Networking' },
];

const TAG_OPTIONS = [
  { id: 'friendship', label: '🫶 Friendship' },
  { id: 'wellness', label: '🧘 Wellness' },
  { id: 'dating', label: '💘 Dating' },
  { id: 'nightlife', label: '🎉 Nightlife' },
  { id: 'art', label: '🎨 Art' },
  { id: 'women-only', label: '👯‍♀️ Women Only' },
  { id: 'men-only', label: '👬 Men Only' },
  { id: 'museum', label: '🏛️ Museum' },
  { id: 'music', label: '🎵 Music' },
  { id: 'sports', label: '🏀 Sports' },
  { id: 'fitness', label: '🏃‍♀️ Fitness' },
  { id: 'networking', label: '🤝 Networking' },
  { id: 'food', label: '🍔 Food' },
  { id: 'wine', label: '🍷 Wine' },
  { id: 'outdoors', label: '🌲 Outdoors' },
  { id: 'movies', label: '🎬 Movies' },
  { id: 'lgbtq', label: '🌈 LGBTQ' },
  { id: 'college', label: '🎓 College' },
  { id: 'learning', label: '📚 Learning' },
];

const VENUE_TYPES = [
  'Bar',
  'Restaurant',
  'Cafe',
  'Park',
  'Museum',
  'Outdoor',
  'Hotel',
  'House',
  'Other',
];

const DRESS_CODES = [
  'Casual',
  'Smart Casual',
  'Business Casual',
  'Cocktail',
  'Semi-Formal',
  'Formal',
  'Black Tie',
  'White Tie',
];

/* ======================
   SMALL HELPERS
====================== */

function buildLockedFormUi(baseUi) {
  const lockedField = {
    backgroundColor: '#eef0f2',
    borderColor: '#e5e7eb',
    color: '#111827',
  };

  return {
    ...baseUi,

    // inputs
    input: { ...baseUi.input, ...lockedField },
    selectInput: { ...baseUi.selectInput, ...lockedField },
    textarea: { ...baseUi.textarea, ...lockedField },
    textareaLarge: { ...baseUi.textareaLarge, ...lockedField },

    // chips / pills / toggles
    chip: { ...baseUi.chip, background: '#eef0f2', cursor: 'default' },
    chipActive: { ...baseUi.chipActive, background: '#eef0f2', cursor: 'default' },
    radioPill: { ...baseUi.radioPill, background: '#eef0f2', cursor: 'default' },

    // size +/- buttons (inside fields)
    sizeBtnBase: { ...baseUi.sizeBtnBase, background: '#eef0f2', cursor: 'default' },
    sizeBtnLeft: { ...baseUi.sizeBtnLeft, background: '#eef0f2', cursor: 'default' },
    sizeBtnRight: { ...baseUi.sizeBtnRight, background: '#eef0f2', cursor: 'default' },
    sizeBtn: { ...baseUi.sizeBtn, background: '#eef0f2', cursor: 'default' },
  };
}

const REVIEW_LOCK_CSS = `
  /* Only affects the form area wrappers we control (not the footer/back buttons) */
  .reviewLocked img {
    opacity: 0.65;
  }

  /* If your image uploader uses a visible file input, hide it in review mode */
  .reviewLocked input[type="file"] {
    display: none !important;
  }

  /* ✅ Allow top-left back button to remain clickable */
  .reviewLocked .backBtn {
    pointer-events: auto !important;
  }
`;


/* ======================
   COMPONENT
====================== */
function to24h(value) {
  if (!value) return '';
  if (/^\d{2}:\d{2}$/.test(value)) return value; // already 24h

  const m = String(value).trim().match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (!m) return '';

  let hh = Number(m[1]);
  const mm = Number(m[2]);
  const ap = m[3].toUpperCase();

  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return '';
  if (hh < 1 || hh > 12 || mm < 0 || mm > 59) return '';

  hh = hh % 12;            // 12 -> 0
  if (ap === 'PM') hh += 12;

  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}


export default function HostAddEventPage() {
const params = useParams();
const eventId = params.eventId || params.id || null;
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
const [agendaComplete, setAgendaComplete] = useState(false);

  const isReviewFlow = Boolean(eventId);

  // Review vs Edit mode (ONLY for review flow)
  const [isEditing, setIsEditing] = useState(!isReviewFlow);

  useEffect(() => {
    setIsEditing(!Boolean(eventId));
  }, [eventId]);

  const reviewLocked = isReviewFlow && !isEditing;

  // Host gate (safe)
  useEffect(() => {
    if (currentUser && !currentUser.isHost) {
      navigate('/events', { replace: true });
    }
  }, [currentUser, navigate]);

  // ─────────────────────────
  // Basic Info (worksheet fields)
  // ─────────────────────────
  const [eventName, setEventName] = useState('');
  const [twist, setTwist] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [startTime, setStartTime] = useState(''); // 24h input, fine for now
  const [endTime, setEndTime] = useState('');
  
  const startTime24 = useMemo(() => to24h(startTime), [startTime]);
const endTime24   = useMemo(() => to24h(endTime), [endTime]);


  const [address, setAddress] = useState('');
  const [locationCoords, setLocationCoords] = useState(null);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressPredictions, setAddressPredictions] = useState([]);
  const [addressEditing, setAddressEditing] = useState(false);
  const [size, setSize] = useState('');
  const [hasEditedAddress, setHasEditedAddress] = useState(false);
  const addressInputRef = useRef(null);
const originalSubmissionRef = useRef(null);

  const [step, setStep] = useState(1);
  const goNext = () => setStep(2);
  const goBack = () => setStep(1);
const [attemptedNext, setAttemptedNext] = useState(false);

useEffect(() => {
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
}, [step]);

  // 🔑 Event gallery (Cloudinary URLs only)
  const [galleryImages, setGalleryImages] = useState([]);

  // Agenda timing (auto + editable)
  const [welcomeStart, setWelcomeStart] = useState('');
  const [welcomeEnd, setWelcomeEnd] = useState('');
  const [openingStart, setOpeningStart] = useState('');
  const [openingEnd, setOpeningEnd] = useState('');
  const [icebreakerStart, setIcebreakerStart] = useState('');
  const [icebreakerEnd, setIcebreakerEnd] = useState('');
  const [transitionStart, setTransitionStart] = useState('');
  const [transitionEnd, setTransitionEnd] = useState('');
  const [closingStart, setClosingStart] = useState('');
  const [closingEnd, setClosingEnd] = useState('');

  // Filters row (worksheet)
const [minAge, setMinAge] = useState('');
const [maxAge, setMaxAge] = useState('');
const [genderGate, setGenderGate] = useState('all');            // ✅ REQUIRED DEFAULT
const [sexualOrientation, setSexualOrientation] = useState('all'); // ✅ REQUIRED DEFAULT
const [affinityText, setAffinityText] = useState('');

  // Purpose tag (worksheet)
  const [purposeTag, setPurposeTag] = useState('');

  // Gender split? (worksheet)
  const [genderSplit, setGenderSplit] = useState('no');

  // Gender split lane caps (counts)
  const [maleCapCount, setMaleCapCount] = useState('');
  const [femaleCapCount, setFemaleCapCount] = useState('');

  // Event tags (worksheet)
  const [eventTags, setEventTags] = useState([]);

  // Venue + dress code (worksheet)
  const [venueType, setVenueType] = useState('');
  const [dressCode, setDressCode] = useState('');

  // Description
  const [description, setDescription] = useState('');

  // ─────────────────────────
  // Agenda fields
  // ─────────────────────────
  const [icebreakerHowItWorks, setIcebreakerHowItWorks] = useState('');
  const [primaryName, setPrimaryName] = useState('');
  const [primaryStart, setPrimaryStart] = useState('');
  const [primaryEnd, setPrimaryEnd] = useState('');
  const [primaryDescription, setPrimaryDescription] = useState('');

  const [secondaryName, setSecondaryName] = useState('');
  const [secondaryStart, setSecondaryStart] = useState('');
  const [secondaryEnd, setSecondaryEnd] = useState('');
  const [secondaryDescription, setSecondaryDescription] = useState('');

  // Submission gate
  const [acknowledgedSafety, setAcknowledgedSafety] = useState(false);
const [submissionStatus, setSubmissionStatus] = useState(null);
const [adminNote, setAdminNote] = useState(null);



  // Keep original submission for Cancel

  const applySubmissionToState = (sub) => {
    if (!sub) return;

    // ===== CORE =====
    setEventName(sub.name || '');
    setTwist(sub.twist || '');
    setDescription(sub.description || '');

    // ===== DATE & TIME =====
    setEventDate(sub.eventDate || '');
    setStartTime(sub.startTime12 || '');
    setEndTime(sub.endTime12 || '');

    // ===== LOCATION =====
    setAddress(sub.address || '');
    setLocationCoords(
      sub.latitude && sub.longitude ? { lat: sub.latitude, lon: sub.longitude } : null
    );

    // reset address UI helpers
    setAddressQuery('');
    setAddressPredictions([]);
    setAddressEditing(false);
    setHasEditedAddress(false);

    // ===== FILTERS =====
    setSize(sub.size || '');
    setMinAge(sub.minAge || '');
    setMaxAge(sub.maxAge || '');
    setGenderGate(sub.genderGate || '');
    setSexualOrientation(sub.sexualOrientation || '');
    setAffinityText(sub.affinityText || '');

    // ===== GENDER SPLIT =====
    setGenderSplit(sub.genderSplit || 'no');
    setMaleCapCount(sub.neutralMaleCap ? String(sub.neutralMaleCap) : '');
    setFemaleCapCount(sub.neutralFemaleCap ? String(sub.neutralFemaleCap) : '');

    // ===== TAGS =====
    setPurposeTag(sub.purposeTag || '');
    setEventTags(Array.isArray(sub.tags) ? sub.tags : []);

    // ===== VENUE =====
    setVenueType(sub.venueType || '');
    setDressCode(sub.clothing || '');

    // ===== IMAGES =====
    setGalleryImages(Array.isArray(sub.galleryImages) ? sub.galleryImages : []);

    // ===== AGENDA =====
    setIcebreakerHowItWorks(sub.icebreakerHowItWorks || '');

    setPrimaryName(sub.primaryName || '');
    setPrimaryStart(sub.primaryStart || '');
    setPrimaryEnd(sub.primaryEnd || '');
    setPrimaryDescription(sub.primaryDescription || '');

    setSecondaryName(sub.secondaryName || '');
    setSecondaryStart(sub.secondaryStart || '');
    setSecondaryEnd(sub.secondaryEnd || '');
    setSecondaryDescription(sub.secondaryDescription || '');

    setWelcomeStart(sub.welcomeStart || '');
    setWelcomeEnd(sub.welcomeEnd || '');
    setOpeningStart(sub.openingStart || '');
    setOpeningEnd(sub.openingEnd || '');
    setTransitionStart(sub.transitionStart || '');
    setTransitionEnd(sub.transitionEnd || '');
    setClosingStart(sub.closingStart || '');
    setClosingEnd(sub.closingEnd || '');
  };

  const cancelEdits = () => {
    applySubmissionToState(originalSubmissionRef.current);
    setIsEditing(false);
  };

  useEffect(() => {
    if (!eventId) return;

    async function loadSubmission() {
      try {
        const token =
          localStorage.getItem('accessToken') ||
          localStorage.getItem('token') ||
          localStorage.getItem('authToken') ||
          '';

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/host/submissions/${eventId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const sub = res.data;
        if (!sub) return;

        originalSubmissionRef.current = sub;
        applySubmissionToState(sub);

        setSubmissionStatus(sub.status || 'pending_review');
setAdminNote(sub.adminNote || sub.reviewNote || null);

      } catch (err) {
        console.error('[HostAddEventPage] failed to load submission', err);
      }
    }

    loadSubmission();
  }, [eventId]);

  const formUi = useMemo(() => {
    return reviewLocked ? buildLockedFormUi(ui) : ui;
  }, [reviewLocked]);



  const selectedTagLabels = useMemo(() => {
    const map = new Map(TAG_OPTIONS.map((t) => [t.id, t.label]));
    return (eventTags || []).map((id) => map.get(id) || id);
  }, [eventTags]);

  // after hooks
  if (!currentUser?.isHost) return null;

  const toggleTag = (id) => {
    setEventTags((prev) => {
      const has = prev.includes(id);
      if (has) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  async function handleAddressChange(e) {
    const val = e.target.value;
    setAddressQuery(val);

    if (!val.trim()) {
      setAddressPredictions([]);
      return;
    }

    try {
      const { addresses } = await Radar.autocomplete({ query: val });
      setAddressPredictions(addresses || []);
    } catch (err) {
      console.error('Radar autocomplete error:', err);
    }
  }

  const buildPayload = () => {
    return {
      // lifecycle
      status: 'pending_review',
      mode: 'neutral',

      // ===== CORE =====
      name: eventName,
      twist,
      description,

      // ===== DATE & TIME =====
      eventDate,
      startTime12: startTime,
      endTime12: endTime,

      // ===== LOCATION =====
      hostedAt: '',
      address,
      latitude: locationCoords?.lat ?? null,
      longitude: locationCoords?.lon ?? null,

      venueType,
      clothing: dressCode,

      // ===== FILTERS =====
      size,
      minAge,
      maxAge,
      genderGate,
      sexualOrientation,

      affinity: Boolean(affinityText && affinityText.trim()),
      affinityText,

      genderSplit,

      neutralMaleCap: genderSplit === 'yes' ? Number(maleCapCount || 0) : 0,
      neutralFemaleCap: genderSplit === 'yes' ? Number(femaleCapCount || 0) : 0,

      // ===== PURPOSE & TAGS =====
      purposeTag,
      tags: eventTags,

      // ===== IMAGES =====
      galleryImages,

      // ===== AGENDA =====
      icebreakerHowItWorks,
      icebreakerStart,
      icebreakerEnd,

      primaryName,
      primaryStart,
      primaryEnd,
      primaryDescription,

      secondaryName,
      secondaryStart,
      secondaryEnd,
      secondaryDescription,

      welcomeStart,
      welcomeEnd,
      openingStart,
      openingEnd,
      transitionStart,
      transitionEnd,
      closingStart,
      closingEnd,
    };
  };

const basicInfoIncomplete = !isBasicInfoComplete();

 function isBasicInfoComplete() {
  return (
    eventName &&
    eventDate &&
    startTime &&
    endTime &&
    address &&
    size &&
    genderGate &&            // 'all' is valid
    sexualOrientation &&     // 'all' is valid
    purposeTag &&
    description &&
    Array.isArray(eventTags) &&
    eventTags.length >= 1 &&
Array.isArray(galleryImages) &&
galleryImages.filter(Boolean).length === 3
  );
}



  function hasChanges() {
  if (!originalSubmissionRef.current) return true; // new submission

  const current = buildPayload();
  const original = originalSubmissionRef.current;

  return Object.keys(current).some((key) => {
    const a = current[key];
    const b = original[key];

    if (Array.isArray(a) && Array.isArray(b)) {
      return JSON.stringify(a) !== JSON.stringify(b);
    }

    return a !== b;
  });
}

const hasUnsavedChanges = !reviewLocked && hasChanges();

const submitSubmission = async () => {
  const token =
    localStorage.getItem('accessToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    '';

  const payload = buildPayload();

  // ✅ THIS IS THE FIX: tells backend which doc to update on resubmit
if (eventId) payload.submissionId = eventId;

  await axios.post(
    `${process.env.REACT_APP_API_URL}/events/submissions`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
};

  return (
    <div style={ui.page}>
      <style>{REVIEW_LOCK_CSS}</style>

      <div style={ui.container}>
        
        {/* =========================
            STEP 1 — BASIC INFO
        ========================== */}
        {step === 1 && (
          <>
<div className={reviewLocked ? 'reviewLocked' : ''}>

              <HostBasicInfo
                ui={formUi}
                isEditing={isEditing}
                reviewLocked={reviewLocked}
                isReviewFlow={isReviewFlow}
                PURPOSE_OPTIONS={PURPOSE_OPTIONS}
                TAG_OPTIONS={TAG_OPTIONS}
                VENUE_TYPES={VENUE_TYPES}
                DRESS_CODES={DRESS_CODES}
                eventName={eventName}
                setEventName={setEventName}
                twist={twist}
                setTwist={setTwist}
                eventDate={eventDate}
                setEventDate={setEventDate}
                startTime={startTime}
                setStartTime={setStartTime}
                endTime={endTime}
                setEndTime={setEndTime}
                address={address}
                setAddress={setAddress}
                locationCoords={locationCoords}
                setLocationCoords={setLocationCoords}
                addressQuery={addressQuery}
                setAddressQuery={setAddressQuery}
                addressPredictions={addressPredictions}
                setAddressPredictions={setAddressPredictions}
                addressEditing={addressEditing}
                setAddressEditing={setAddressEditing}
                size={size}
                setSize={setSize}
                hasEditedAddress={hasEditedAddress}
                setHasEditedAddress={setHasEditedAddress}
                addressInputRef={addressInputRef}
                minAge={minAge}
                setMinAge={setMinAge}
                maxAge={maxAge}
                setMaxAge={setMaxAge}
                genderGate={genderGate}
                setGenderGate={setGenderGate}
                sexualOrientation={sexualOrientation}
                setSexualOrientation={setSexualOrientation}
                affinityText={affinityText}
                setAffinityText={setAffinityText}
                purposeTag={purposeTag}
                setPurposeTag={setPurposeTag}
                genderSplit={genderSplit}
                setGenderSplit={setGenderSplit}
                maleCapCount={maleCapCount}
                setMaleCapCount={setMaleCapCount}
                femaleCapCount={femaleCapCount}
                setFemaleCapCount={setFemaleCapCount}
                eventTags={eventTags}
                toggleTag={toggleTag}
                selectedTagLabels={selectedTagLabels}
                venueType={venueType}
                setVenueType={setVenueType}
                dressCode={dressCode}
                setDressCode={setDressCode}
                description={description}
                setDescription={setDescription}
                galleryImages={galleryImages}
                // IMPORTANT: passing undefined in reviewLocked lets HostBasicInfo
                // hide upload controls if it checks for this prop.
                setGalleryImages={reviewLocked ? undefined : setGalleryImages}
                handleAddressChange={handleAddressChange}
  onEdit={() => setIsEditing(true)}
  onCancel={cancelEdits}
attemptedNext={attemptedNext}

    submissionStatus={submissionStatus}
  adminNote={adminNote}
              />
            </div>

            {/* Footer (DO NOT pass locked styles to footer) */}
          {isReviewFlow ? (
  <EventStepFooter
  ui={ui}
  showNext
  nextDisabled={basicInfoIncomplete}   // ✅ ADD THIS
  onNext={() => {
    setAttemptedNext(true);

    if (basicInfoIncomplete) return;   // 🚫 BLOCK

    goNext();
  }}
  submissionStatus={null}
/>

) : (
<EventStepFooter
  hasUnsavedChanges={true}
  ui={ui}
  showNext
  nextDisabled={basicInfoIncomplete}   // ✅ THIS WAS MISSING
  onNext={() => {
    setAttemptedNext(true);

    if (basicInfoIncomplete) return;

    goNext();
  }}
  submissionStatus={null}
/>

)}

          </>
        )}

        {/* =========================
            STEP 2 — AGENDA
        ========================== */}
        {step === 2 && (
          <>
<div className={reviewLocked ? 'reviewLocked' : ''}>

              <HostAgenda
                ui={formUi}
                isEditing={isEditing}
                reviewLocked={reviewLocked}
                isReviewFlow={isReviewFlow}
                step={2}
                totalSteps={2}
           eventStartTime={startTime24}
eventEndTime={endTime24}
                
                welcomeStart={welcomeStart}
                setWelcomeStart={setWelcomeStart}
                welcomeEnd={welcomeEnd}
                setWelcomeEnd={setWelcomeEnd}
                openingStart={openingStart}
                setOpeningStart={setOpeningStart}
                openingEnd={openingEnd}
                setOpeningEnd={setOpeningEnd}
                icebreakerStart={icebreakerStart}
                setIcebreakerStart={setIcebreakerStart}
                icebreakerEnd={icebreakerEnd}
                setIcebreakerEnd={setIcebreakerEnd}
                transitionStart={transitionStart}
                setTransitionStart={setTransitionStart}
                transitionEnd={transitionEnd}
                setTransitionEnd={setTransitionEnd}
                closingStart={closingStart}
                setClosingStart={setClosingStart}
                closingEnd={closingEnd}
                setClosingEnd={setClosingEnd}
                icebreakerHowItWorks={icebreakerHowItWorks}
                setIcebreakerHowItWorks={setIcebreakerHowItWorks}
                primaryName={primaryName}
                setPrimaryName={setPrimaryName}
                primaryStart={primaryStart}
                setPrimaryStart={setPrimaryStart}
                primaryEnd={primaryEnd}
                setPrimaryEnd={setPrimaryEnd}
                primaryDescription={primaryDescription}
                setPrimaryDescription={setPrimaryDescription}
                secondaryName={secondaryName}
                setSecondaryName={setSecondaryName}
                secondaryStart={secondaryStart}
                setSecondaryStart={setSecondaryStart}
                secondaryEnd={secondaryEnd}
                setSecondaryEnd={setSecondaryEnd}
                secondaryDescription={secondaryDescription}
                setSecondaryDescription={setSecondaryDescription}
                acknowledgedSafety={acknowledgedSafety}
                setAcknowledgedSafety={setAcknowledgedSafety}
                  onEdit={() => setIsEditing(true)}
  onCancel={cancelEdits}
   submissionStatus={submissionStatus}
   setAgendaComplete={setAgendaComplete}

              />
            </div>

   <EventStepFooter
  ui={ui}
  showBack
  showSubmit
  reviewLocked={false}
  submissionStatus={submissionStatus}   // ← keeps status pill correct
  hasUnsavedChanges={hasUnsavedChanges} // ← used for UI logic
  onBack={goBack}
  onSubmit={async () => {
    try {
      await submitSubmission(); // ✅ handles new + resubmit (adds submissionId when editing)
      alert('Submitted for admin review');
      navigate('/host');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || err.message);
    }
  }}
  submitDisabled={!acknowledgedSafety || !agendaComplete}
/>



          </>
        )}
      </div>
    </div>
  );
}

/* ===============================
   CLEAN, SHARED UI STYLES
================================ */

const ui = {
  page: {
    background: '#fff',
    minHeight: '100vh',
    color: '#0b0b0b',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  container: {
    maxWidth: 760,
    margin: '12px auto 0',
    boxSizing: 'border-box',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    marginTop: 12,
    marginBottom: 10,
    marginLeft: 0,
    color: '#0b0b0b',
    fontWeight: 900,
    cursor: 'pointer',
  },
  pageTitle: {
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: 0.2,
    margin: '6px 0 12px',
    textTransform: 'uppercase',
  },

  block: {
    borderTop: '1px solid #eef0f2',
    paddingTop: 14,
    marginTop: 14,
  },
  blockHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#000',
  },

  fieldLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: '#374151',
    letterSpacing: 0.2,
    marginBottom: 6,
  },

  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 10,
  },

input: {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  backgroundColor: '#fff',
  outline: 'none',
  marginBottom: 10,

  // smaller + lighter, consistent across all fields
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 450,
  letterSpacing: 0.6,
  color: '#0b0b0b',
},

// ✅ REPLACE ui.selectInput with this
selectInput: {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 40px 10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  backgroundColor: '#fff',
  outline: 'none',
  marginBottom: 10,

  // match input
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 450,
  letterSpacing: 0.6,
  color: '#0b0b0b',
},

// ✅ REPLACE ui.textarea with this
textarea: {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  backgroundColor: '#fff',
  outline: 'none',
  minHeight: 90,
  resize: 'vertical',

  // match input
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 450,
  letterSpacing: 0.6,
  color: '#0b0b0b',
},

// ✅ REPLACE ui.textareaLarge with this
textareaLarge: {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  backgroundColor: '#fff',
  outline: 'none',
  minHeight: 140,
  resize: 'vertical',

  // match input
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 450,
  letterSpacing: 0.6,
  color: '#0b0b0b',
},


  chipWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 8,
  },
  chip: {
    padding: '8px 12px',
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    background: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 450,
  letterSpacing: 0.6,
  color: '#0b0b0b',
  },
  chipActive: {
    padding: '8px 12px',
    borderRadius: 999,
    border: '1px solid #0b0b0b',
    background: '#f8fafc',
    cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 550,
  letterSpacing: 0.6,
  color: '#0b0b0b',
  },

  miniMeta: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 6,
  },

  divider: {
    height: 1,
    backgroundColor: '#eef0f2',
    margin: '14px 0',
  },

  agendaSection: {
    padding: '10px 0',
  },
  agendaTitle: {
    fontSize: 14,
    fontWeight: 900,
    marginBottom: 6,
  },
  agendaPurpose: {
    fontSize: 13,
    color: '#111827',
    lineHeight: 1.45,
  },
  agendaMeta: {
    fontSize: 12.5,
    color: '#6b7280',
    marginTop: 4,
  },
  agendaBody: {
    fontSize: 13,
    color: '#111827',
    marginTop: 8,
    lineHeight: 1.55,
  },

  checkRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: 13,
    color: '#111827',
    marginTop: 10,
  },

  inlineRadios: {
    display: 'flex',
    gap: 10,
    marginTop: 10,
  },
  radioPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 800,
  },

  primaryBtn: {
    width: '100%',
    height: 44,
    borderRadius: 10,
    border: 'none',
    background: '#0b0b0b',
    color: '#fff',
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
    marginTop: 14,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
  primaryBtnDisabled: {
    background: '#bdbdbd',
    cursor: 'not-allowed',
  },

  sizeBtnBase: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 24,
    height: 24,
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#111827',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },

  sizeBtnLeft: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    left: 8,
    width: 24,
    height: 24,
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#111827',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },

  sizeBtnRight: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: 8,
    width: 24,
    height: 24,
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#111827',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },

  sizeBtn: {
    height: 38,
    minWidth: 38,
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#111827',
    fontSize: 18,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },

  sizeBtnDisabled: {
    background: '#f3f4f6',
    color: '#9ca3af',
    cursor: 'not-allowed',
  },
};
